import React from "react";
import PdfButton from "../../../ui/PdfButton.jsx";
import { CoatingSwatch } from "../../../../components/CoatingColor";
import {
  atVendorFacts, fmtDate, fmtMoney, fmtQty, isDeadLine, isManifest, lineBack, lineIdentity, paperDocument,
} from "../../../../features/outsourcing/coatingData";

// The phone Outsourcing tab's papers as cards (the desktop's Manifests / At Vendor / History tabs in operator
// mode): Loading = Draft papers (lines, take a line off, Send, the paper, pallet labels, Find pallets); At vendor =
// what is out (Receive back, the paper again, pallet labels, Beacons back on); History = done papers (the paper
// again). The office's tools (vendor email, invoice entry, Delete draft) stay on the desktop. Layout only.

export function StatusTag({ status }) {
  const slug = String(status || "").split(" ")[0];
  return <span className={`jq-out-status jq-out-status-${slug}`}>{status}</span>;
}

function SoTag({ opo }) {
  return <span className="jq-tag">{opo.sales_order_number ? `SO ${opo.sales_order_number}` : "Internal"}</span>;
}

export function LineIdentity({ l }) {
  const id = lineIdentity(l);
  return (
    <>
      {id.assembly ? <><span className="jq-tag me-1">ASSY</span><span className="fw-semibold">{id.assembly}</span>{" — "}</> : null}
      {id.text}
      {id.job ? <span className="jq-muted"> · Job {id.job}</span> : null}
    </>
  );
}

function CardHead({ opo, right = null }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <span className="fw-bold" style={{ fontFamily: "monospace", fontSize: "1.05rem" }}>{opo.opo_number}</span>
      <StatusTag status={opo.status} />
      <SoTag opo={opo} />
      <span className="ms-auto">{right}</span>
      <div className="w-100 small">{opo.vendor_name || <span className="jq-muted">no vendor</span>}</div>
    </div>
  );
}

function PaperButton({ apiBase, opo, label, testId }) {
  const doc = paperDocument(apiBase, opo);
  return <PdfButton url={doc.url} filename={doc.filename} label={label} className="btn btn-outline-secondary w-100" testId={testId} />;
}

/** A Draft paper on the Loading tab. */
export function DraftCard({ apiBase, opo, busy, onSend, onRemoveLine, onPalletLabels, onFindPallets }) {
  const dead = (opo.lines || []).filter(isDeadLine);
  return (
    <div className="jq-card" data-testid={`phone-out-draft-${opo.opo_id}`}>
      <CardHead opo={opo} right={<span className="fw-semibold">{fmtMoney(opo.estimated_total)}</span>} />
      <div className="mt-2">
        {(opo.lines || []).map((l) => {
          const isDead = isDeadLine(l);
          return (
            <div className={`jq-out-line${isDead ? " dead" : ""}`} key={l.opo_line_id}>
              <span className="jq-muted small" style={{ minWidth: 18 }}>{l.line_number}</span>
              <div className="jq-out-line-body">
                <div><LineIdentity l={l} /></div>
                <div className="small d-flex flex-wrap gap-2">
                  <span className="fw-semibold">{fmtQty(l.quantity_sent)} pcs</span>
                  {l.color_code ? <CoatingSwatch code={l.color_code} name={l.color_name} hex={l.color_hex} />
                    : <span className="text-danger">color not set</span>}
                </div>
                {isDead ? (
                  <div className="small text-danger">task {String(l.task_status || "").toLowerCase()} — take this line off before sending</div>
                ) : null}
              </div>
              {l.job_task_id ? (
                <button type="button" className="jq-icon-btn text-danger" disabled={busy}
                  aria-label={`Take line ${l.line_number} off ${opo.opo_number}`} onClick={() => onRemoveLine(opo, l)}>
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      {dead.length ? <div className="jq-note jq-note-warn mt-2 mb-0">Take the voided / cancelled lines off before sending.</div> : null}
      <div className="jq-out-buttons">
        <button type="button" className="btn btn-success btn-lg jq-out-wide" disabled={busy} onClick={() => onSend(opo)}>
          Send to {opo.vendor_name || "the vendor"}
        </button>
        <PaperButton apiBase={apiBase} opo={opo} label="The paper" testId={`phone-out-paper-${opo.opo_id}`} />
        <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => onPalletLabels(opo)}>
          Pallet labels
        </button>
        <button type="button" className="btn btn-outline-primary jq-out-wide" disabled={busy} onClick={() => onFindPallets(opo)}>
          Find pallets
        </button>
      </div>
    </div>
  );
}

/** A paper out at a vendor (At vendor tab). */
export function AtVendorCard({ apiBase, opo, busy, onReceive, onPalletLabels, onBeaconsBackOn }) {
  const { daysOut, pastDue } = atVendorFacts(opo);
  return (
    <div className="jq-card" data-testid={`phone-out-vendor-${opo.opo_id}`}>
      <CardHead opo={opo} right={daysOut != null ? (
        <span className={`jq-tag ${pastDue ? "jq-tag-block" : ""}`}>{daysOut} d at vendor</span>
      ) : null} />
      {!isManifest(opo) ? <div className="small jq-muted">manual OPO: receiving records quantities only</div> : null}
      <div className="jq-out-facts">
        <span><span className="jq-muted">Sent:</span> {fmtDate(opo.sent_at)}</span>
        <span className={pastDue ? "text-danger fw-semibold" : ""}><span className="jq-muted">Due back:</span> {fmtDate(opo.due_back)}</span>
        <span><span className="jq-muted">At vendor:</span> {fmtQty(opo.units_at_vendor)}</span>
        {opo.color_summary ? <span><span className="jq-muted">Color:</span> {opo.color_summary}</span> : null}
      </div>
      {(opo.lines || []).map((l) => (
        <div className="jq-out-line" key={l.opo_line_id}>
          <span className="jq-muted small" style={{ minWidth: 18 }}>{l.line_number}</span>
          <div className="jq-out-line-body"><LineIdentity l={l} /></div>
          <span className="small text-end" style={{ whiteSpace: "nowrap" }}>
            {fmtQty(lineBack(l))} / {fmtQty(l.quantity_sent)}
            {Number(l.quantity_scrapped || 0) > 0 ? <div className="text-danger">({fmtQty(l.quantity_scrapped)} scrap)</div> : null}
          </span>
        </div>
      ))}
      <div className="jq-out-buttons">
        <button type="button" className="btn btn-success btn-lg jq-out-wide" disabled={busy} onClick={() => onReceive(opo)}>
          Receive back
        </button>
        <PaperButton apiBase={apiBase} opo={opo} label="Reprint" testId={`phone-out-reprint-${opo.opo_id}`} />
        <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => onPalletLabels(opo)}>
          Pallet labels
        </button>
        <button type="button" className="btn btn-outline-primary jq-out-wide" disabled={busy} onClick={() => onBeaconsBackOn(opo)}>
          Beacons back on
        </button>
      </div>
    </div>
  );
}

/** A returned / closed / cancelled paper (History tab). */
export function HistoryCard({ apiBase, opo }) {
  return (
    <div className="jq-card" data-testid={`phone-out-history-${opo.opo_id}`}>
      <CardHead opo={opo} right={!isManifest(opo) ? <span className="jq-tag">manual OPO</span> : null} />
      <div className="jq-out-facts">
        <span><span className="jq-muted">Returned:</span> {fmtDate(opo.returned_at)}</span>
        <span>
          <span className="jq-muted">Cost:</span>{" "}
          {opo.actual_total != null ? fmtMoney(opo.actual_total) : <>{fmtMoney(opo.estimated_total)} <span className="jq-muted">(est)</span></>}
        </span>
        <span><span className="jq-muted">Invoice #:</span> {opo.vendor_invoice_number || "—"}</span>
      </div>
      <PaperButton apiBase={apiBase} opo={opo} label="Reprint" testId={`phone-out-reprint-${opo.opo_id}`} />
    </div>
  );
}
